import { useCallback } from 'react'
import type { MouseEvent } from 'react'
import { CropperOverlay, CropHandle } from './ImageCropper.styled'

type ImageCropperProps = {
  width: number
  height: number
  cropLeft: number
  cropRight: number
  cropTop: number
  cropBottom: number
  onCropChange: (crop: {
    left: number
    right: number
    top: number
    bottom: number
  }) => void
}

type cropEdge = 'left' | 'right' | 'top' | 'bottom'

const MIN_CROP_SIZE = 50 // Minimum size in pixels for cropped area

export const ImageCropper = ({
  width,
  height,
  cropLeft,
  cropRight,
  cropTop,
  cropBottom,
  onCropChange,
}: ImageCropperProps) => {
  const handleDrag = useCallback(
    (edge: cropEdge, startEvent: MouseEvent) => {
      startEvent.preventDefault()
      startEvent.stopPropagation()
      const startX = startEvent.clientX
      const startY = startEvent.clientY
      const startCrop = {
        left: cropLeft,
        right: cropRight,
        top: cropTop,
        bottom: cropBottom,
      }

      const handleMouseMove = (moveEvent: globalThis.MouseEvent) => {
        moveEvent.preventDefault()
        moveEvent.stopPropagation()
        const deltaX = moveEvent.clientX - startX
        const deltaY = moveEvent.clientY - startY
        const newCrop = { ...startCrop }

        switch (edge) {
          case 'left':
            newCrop.left = Math.max(
              0,
              Math.min(
                startCrop.left + deltaX,
                width - startCrop.right - MIN_CROP_SIZE,
              ),
            )
            break
          case 'right':
            newCrop.right = Math.max(
              0,
              Math.min(
                startCrop.right - deltaX,
                width - startCrop.left - MIN_CROP_SIZE,
              ),
            )
            break
          case 'top':
            newCrop.top = Math.max(
              0,
              Math.min(
                startCrop.top + deltaY,
                height - startCrop.bottom - MIN_CROP_SIZE,
              ),
            )
            break
          case 'bottom':
            newCrop.bottom = Math.max(
              0,
              Math.min(
                startCrop.bottom - deltaY,
                height - startCrop.top - MIN_CROP_SIZE,
              ),
            )
            break
        }
        onCropChange(newCrop)
      }

      const handleMouseUp = (upEvent: globalThis.MouseEvent) => {
        upEvent.preventDefault()
        upEvent.stopPropagation()
        cleanup()
      }

      const handleWindowBlur = () => {
        cleanup()
      }

      const cleanup = () => {
        document.removeEventListener('mousemove', handleMouseMove)
        document.removeEventListener('mouseup', handleMouseUp)
        window.removeEventListener('blur', handleWindowBlur)
      }

      document.addEventListener('mousemove', handleMouseMove)
      document.addEventListener('mouseup', handleMouseUp)
      window.addEventListener('blur', handleWindowBlur)
    },
    [cropLeft, cropRight, cropTop, cropBottom, width, height, onCropChange],
  )

  // Only show overlay if the cropped area is valid and meets minimum size requirements
  if (
    width - cropLeft - cropRight < MIN_CROP_SIZE ||
    height - cropTop - cropBottom < MIN_CROP_SIZE
  )
    return null

  return (
    <CropperOverlay
      cropLeft={cropLeft}
      cropTop={cropTop}
      cropRight={cropRight}
      cropBottom={cropBottom}
      width={width}
      height={height}
    >
      <CropHandle
        className="left"
        onMouseDown={(e: MouseEvent) => handleDrag('left', e)}
      />
      <CropHandle
        className="right"
        onMouseDown={(e: MouseEvent) => handleDrag('right', e)}
      />
      <CropHandle
        className="top"
        onMouseDown={(e: MouseEvent) => handleDrag('top', e)}
      />
      <CropHandle
        className="bottom"
        onMouseDown={(e: MouseEvent) => handleDrag('bottom', e)}
      />
    </CropperOverlay>
  )
}
